import React from 'react'
import {
  Container,
  Typography,
  Grid,
  Stack,
  Avatar,
  Box,
  Button,
  Divider,
  Card,
  CardContent,
  Chip,
  useTheme,
  Fade,
  Zoom
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  RocketLaunch,
  Architecture,
  Build,
  Language,
  MiscellaneousServices,
  SupportAgent,
  ArrowForward,
  CheckCircle,
  Star
} from '@mui/icons-material'
import { services } from '../data/services'
import { alpha } from '@mui/material/styles'
import Layout from '../components/Layout'
import { useColorMode } from '../theme'

const iconMap: Record<string, React.ReactNode> = {
  RocketLaunch: <RocketLaunch />,
  Architecture: <Architecture />,
  Language: <Language />,
  MiscellaneousServices: <MiscellaneousServices />,
  Build: <Build />,
  SupportAgent: <SupportAgent />,
}

const Leistungen: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { mode } = useColorMode()
  const isDark = mode === 'dark'
  const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
  const glassBorder = `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.18)}`
  const heroOverlay = `linear-gradient(135deg, ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.08)} 0%, ${alpha(theme.palette.secondary.main, isDark ? 0.15 : 0.08)} 100%)`
  const cardBackground = isDark
    ? `linear-gradient(135deg, ${alpha('#ffffff', 0.08)} 0%, ${alpha('#ffffff', 0.04)} 100%)`
    : `linear-gradient(135deg, ${alpha('#ffffff', 0.96)} 0%, ${alpha('#f4f6ff', 0.92)} 100%)`
  const subtleText = isDark
    ? alpha(theme.palette.common.white, 0.78)
    : alpha(theme.palette.text.primary, 0.72)
  const mutedText = isDark
    ? alpha(theme.palette.common.white, 0.82)
    : alpha(theme.palette.text.primary, 0.8)

  return (
    <Layout>
      <Box sx={{
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '300px',
          background: heroOverlay,
          zIndex: 0
        }
      }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1 }}>
          {/* Hero Section */}
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
              <Chip
                icon={<Star />}
                label={t('servicesTitle')}
                sx={{
                  mb: 4,
                  bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.16),
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 3,
                  py: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.28)}`
                }}
              />
              <Typography
                variant="h2"
                component="h1"
                fontWeight={900}
                sx={{
                  mb: 4,
                  background: primaryGradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2.8rem', md: '4rem' },
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em'
                }}
              >
                {t('servicesTitle')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 6,
                  maxWidth: 900,
                  mx: 'auto',
                  lineHeight: 1.7,
                  fontWeight: 400,
                  color: subtleText,
                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                }}
              >
                {t('servicesSubtitle')}
              </Typography>
            </Box>
          </Fade>

          {/* Services Grid */}
          <Grid container spacing={{ xs: 3, md: 5 }}>
            {services.map((s, i) => (
              <Grid key={i} item xs={12} md={6}>
                <Zoom in timeout={600 + i * 100}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      background: cardBackground,
                      backdropFilter: 'blur(20px)',
                      border: glassBorder,
                      borderRadius: 4,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-12px)',
                        boxShadow: isDark
                          ? `0 25px 50px ${alpha(theme.palette.primary.main, 0.25)}`
                          : `0 25px 50px ${alpha(theme.palette.primary.main, 0.18)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 4, md: 5 } }}>
                      <Stack direction="row" spacing={4} alignItems="flex-start" sx={{ mb: 4 }}>
                        <Avatar
                          sx={{
                            width: 64,
                            height: 64,
                            backgroundImage: primaryGradient,
                            backgroundColor: 'transparent',
                            color: theme.palette.common.white,
                            boxShadow: isDark
                              ? `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`
                              : `0 12px 32px ${alpha(theme.palette.primary.main, 0.28)}`,
                            border: `2px solid ${alpha('#ffffff', isDark ? 0.16 : 0.3)}`
                          }}
                        >
                          {iconMap[s.icon]}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{
                              mb: 2,
                              color: isDark ? theme.palette.common.white : theme.palette.text.primary,
                              fontSize: { xs: '1.6rem', md: '1.9rem' },
                              lineHeight: 1.2
                            }}
                          >
                            {t(s.titleKey)}
                          </Typography>
                          <Typography
                            sx={{
                              mb: 4,
                              lineHeight: 1.7,
                              fontSize: '1.15rem',
                              color: subtleText,
                              fontWeight: 400
                            }}
                          >
                            {t(s.textKey)}
                          </Typography>
                        </Box>
                      </Stack>

                      {s.details && s.details.length > 0 && (
                        <Box sx={{ mb: 5 }}>
                          <Stack spacing={3}>
                            {s.details.map((d, idx) => (
                              <Stack key={idx} direction="row" spacing={3} alignItems="flex-start">
                                <CheckCircle
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: '1.4rem',
                                    flexShrink: 0,
                                    mt: 0.2,
                                    filter: isDark ? 'drop-shadow(0 2px 4px rgba(108, 92, 231, 0.3))' : 'none'
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '1.1rem',
                                    lineHeight: 1.6,
                                    color: mutedText,
                                    fontWeight: 400
                                  }}
                                >
                                  {d}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      <Divider sx={{
                        my: 4,
                        borderColor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.16),
                        borderWidth: 1
                      }} />

                      <Button
                        href={`mailto:hallo@webmix-it.de?subject=${encodeURIComponent(t('inquirySubjectPrefix'))}%20${encodeURIComponent(t(s.titleKey))}`}
                        variant="contained"
                        endIcon={<ArrowForward />}
                        fullWidth
                        sx={{
                          py: 2,
                          px: 3,
                          borderRadius: 3,
                          background: primaryGradient,
                          boxShadow: isDark
                            ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`
                            : `0 8px 24px ${alpha(theme.palette.primary.main, 0.28)}`,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          '&:hover': {
                            boxShadow: isDark
                              ? `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`
                              : `0 12px 32px ${alpha(theme.palette.primary.main, 0.36)}`,
                            transform: 'translateY(-3px)'
                          },
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        {t('inquire')}
                      </Button>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>

          {/* Call to Action Section */}
          <Fade in timeout={1200}>
            <Box
              sx={{
                mt: { xs: 10, md: 12 },
                p: { xs: 6, md: 8 },
                borderRadius: 6,
                background: heroOverlay,
                border: `2px solid ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.24)}`,
                textAlign: 'center',
                backdropFilter: 'blur(20px)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                  zIndex: 0
                }
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="h3"
                  fontWeight={900}
                  sx={{
                    mb: 3,
                    background: primaryGradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '2.2rem', md: '3rem' },
                    lineHeight: 1.1
                  }}
                >
                  {t('servicesCtaTitle')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 6,
                    maxWidth: 700,
                    mx: 'auto',
                    color: subtleText,
                    fontSize: { xs: '1.2rem', md: '1.4rem' },
                    lineHeight: 1.6,
                    fontWeight: 400
                  }}
                >
                  {t('servicesCtaSubtitle')}
                </Typography>
                <Button
                  href="mailto:hallo@webmix-it.de?subject=Beratungsanfrage"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    py: 3,
                    px: 6,
                    borderRadius: 4,
                    background: primaryGradient,
                    boxShadow: isDark
                      ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`
                      : `0 12px 40px ${alpha(theme.palette.primary.main, 0.3)}`,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': {
                      boxShadow: isDark
                        ? `0 16px 48px ${alpha(theme.palette.primary.main, 0.5)}`
                        : `0 16px 48px ${alpha(theme.palette.primary.main, 0.38)}`,
                      transform: 'translateY(-4px)'
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {t('requestConsultation')}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Layout>
  )
}

export default Leistungen
